// @flow

import React from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import * as RenterApp from 'styles/Home/StyledRenterApplication';
import BackButton from 'components/renter_application/BackButtonComponent';
import UploadFiles from 'components/renter_application/UploadFilesComponent';
import IdCard from 'AssetsRoot/images/homie_v3/id_card.svg.react';
import Asset from 'models/homes/Asset';
import HomeApplicationActions from 'actions/HomeApplicationActions';
import HomeApplicationStore from 'stores/HomeApplicationStore';
import { NATURAL_PERSON_CONST } from 'utils/Constants';
import {
  isValidFileType,
  isValidFileSize,
  maxFileSizeMessage,
  ValidFileTypesOnDocuments
} from 'validations/HomieValidations';
import { Form, Formik } from 'formik';
import PaymentMethodStore from 'stores/PaymentMethodStore';
import PaymentMethodActions from 'actions/PaymentMethodActions';

const StyledForm = styled.form`
  padding-top: ${props => (props.addPadding ? 70 : 0)}px;
`;

const BackButtonContainer = styled(RenterApp.BackButtonContainer)`
  margin-top: ${({ addMargin }) => (addMargin ? 20 : 0)}px;
  margin-left: ${({ addMargin }) => (addMargin ? 20 : 0)}px;
`;

const Title = styled(RenterApp.Title)`
  margin-bottom: 10px;
`;

const IdCardIcon = styled(IdCard)`
  & g {
    fill: ${({ color }) => color};
  }
`;

const IdCardIconRed = styled(IdCardIcon)`
  & g {
    fill: #ff1a00;
  }
`;

const ErrorMessage = styled.span`
  display: block;
  height: ${props => (props.visible ? '15px' : '0')};
  width: 183px;
  color: #ff1a00;
  font-size: 12px;
  line-height: 15px;
  margin-top: ${props => (props.visible ? '4px' : '0')};
  margin-bottom: 30px;
  transition: all 0.2s linear;
  width: 100%;
`;

const CancelAppButton = styled(RenterApp.CancelAppButton)`
  height: ${({ isForAdvisor }) => (isForAdvisor ? '24px' : 'auto')};
  top: ${({ isForAdvisor }) => (isForAdvisor ? '22px' : '0')};
  right: ${({ isForAdvisor }) => (isForAdvisor ? '15px' : '0')};
`;

type PropsType = {
  homeApplication: any,
  onChange: (any, any) => void,
  onCancel: () => void,
  nextAction: () => void,
  onPrevStep: () => void,
  isForAdvisor?: boolean,
  appType: string,
  color?: string
};

type State = {
  idFilesLoaded: Array<Asset>,
  earningsFilesLoaded: Array<Asset>,
  idFilesValidationError: boolean,
  earningsFilesValidationError: boolean,
  errorMessage: string,
  succesCode: null,
  errorCode: null,
  errorPaymentPromo: boolean,
  correctPayment: boolean,
  paymentData: any
};

export default class DocumentsContentComponent extends React.Component<
  PropsType,
  State
> {
  static defaultProps = {
    isForAdvisor: false,
    color: '#278C7E'
  };

  state = {
    idFilesLoaded: [],
    earningsFilesLoaded: [],
    idFilesValidationError: false,
    earningsFilesValidationError: false,
    errorMessage: '',
    succesCode: null,
    errorCode: null,
    errorPaymentPromo: false,
    correctPayment: false,
    paymentData: null
  };

  componentWillMount = (): void => {
    HomeApplicationStore.addAssetsChangeListener(this.onAssetsLoaded);
  };

  componentDidMount = (): void => {
    const application_id = _.get(this.props.homeApplication, 'id');
    HomeApplicationActions.getMaxAssetSize();
    HomeApplicationActions.listHomeApplicationAssets(
      application_id,
      'identifications'
    );
    HomeApplicationActions.listHomeApplicationAssets(
      application_id,
      'proofs_of_income'
    );
  };

  componentWillUnmount = (): void => {
    HomeApplicationStore.removeAssetsChangeListener(this.onAssetsLoaded);
  };

  onAssetsLoaded = (): void => {
    const uploadError = HomeApplicationStore.getAssetUploadError();

    this.setState({
      idFilesLoaded: HomeApplicationStore.getHomeApplicationAssetsByType(
        'identifications'
      ),
      earningsFilesLoaded: HomeApplicationStore.getHomeApplicationAssetsByType(
        'proofs_of_income'
      ),
      ...(uploadError
        ? {
            errorMessage: uploadError.message,
            idFilesValidationError:
              uploadError.asset_type === 'identifications',
            earningsFilesValidationError:
              uploadError.asset_type === 'proofs_of_income'
          }
        : {})
    });
  };

  _uploadIdFile = (file: File, assetTmpId: string): void => {
    if (isValidFileType(file, ValidFileTypesOnDocuments)) {
      HomeApplicationActions.uploadAsset(
        this.props.homeApplication.id,
        'identifications',
        file,
        assetTmpId
      );
    }
  };

  _uploadEarningsFile = (file: File, assetTmpId: string): void => {
    if (isValidFileType(file, ValidFileTypesOnDocuments)) {
      HomeApplicationActions.uploadAsset(
        this.props.homeApplication.id,
        'proofs_of_income',
        file,
        assetTmpId
      );
    }
  };

  _onSelectIdFiles = (files: Array<File>): void => {
    let assetsLoadedTemp = JSON.parse(JSON.stringify(this.state.idFilesLoaded));
    for (let i = 0; i < files.length; ++i) {
      if (isValidFileType(files[i], ValidFileTypesOnDocuments)) {
        const maxAssetSize = HomeApplicationStore.getMaxAssetSize();
        if (!isValidFileSize(files[i], maxAssetSize)) {
          this.setState({
            idFilesValidationError: true,
            errorMessage: maxFileSizeMessage(maxAssetSize)
          });
          continue;
        }
        this.setState({ idFilesValidationError: false });
        const file = files[i];
        if (
          NATURAL_PERSON_CONST.MAX_IDENTIFICATIONS > assetsLoadedTemp.length
        ) {
          const assetLoaded = new Asset(
            `id${i}`,
            '',
            '',
            'identifications',
            file
          );
          HomeApplicationActions.addTmpAsset(assetLoaded);
        } else {
          break;
        }
      } else {
        this.setState({
          idFilesValidationError: true,
          errorMessage: 'Ingresa un tipo de archivo válido.'
        });
      }
    }
  };

  _onSelectEarningsFiles = (files: Array<File>): void => {
    for (let i = 0; i < files.length; ++i) {
      if (isValidFileType(files[i], ValidFileTypesOnDocuments)) {
        const maxAssetSize = HomeApplicationStore.getMaxAssetSize();
        if (!isValidFileSize(files[i], maxAssetSize)) {
          this.setState({
            earningsFilesValidationError: true,
            errorMessage: maxFileSizeMessage(maxAssetSize)
          });
          continue;
        }
        this.setState({ earningsFilesValidationError: false });
        const file = files[i];
        const assetLoaded = new Asset(
          `poi${i}`,
          '',
          '',
          'proofs_of_income',
          file
        );
        HomeApplicationActions.addTmpAsset(assetLoaded);
      } else {
        this.setState({
          earningsFilesValidationError: true,
          errorMessage: 'Ingresa un tipo de archivo válido.'
        });
      }
    }
  };

  _handleDeleteIdFileLoaded = (id: string | number): void => {
    HomeApplicationActions.deleteAsset(
      this.props.homeApplication.id,
      id,
      'identifications'
    );
  };

  _handleDeleteEarningsFileLoaded = (id: string | number): void => {
    HomeApplicationActions.deleteAsset(
      this.props.homeApplication.id,
      id,
      'proofs_of_income'
    );
  };

  checkValidation = (): boolean => {
    let validationSuccess = true;
    if (this.state.idFilesLoaded.length <= 0) {
      this.setState({
        idFilesValidationError: true,
        errorMessage: 'Al menos una imágen es requerida.'
      });
      validationSuccess = false;
    }
    return validationSuccess;
  };

  resolveValidationFile = (SizeFiles: number): boolean => {
    return (
      SizeFiles < NATURAL_PERSON_CONST.MIN_IMAGES ||
      SizeFiles > NATURAL_PERSON_CONST.MAX_IDENTIFICATIONS
    );
  };

  resolveEarningsFileValidation = (files: number): boolean => {
    return files < NATURAL_PERSON_CONST.MIN_IMAGES;
  };

  renderCancelButton = () => {
    const { onCancel, isForAdvisor, color } = this.props;
    return (
      !isForAdvisor && (
        <CancelAppButton onClick={onCancel} color={color}>
          Cancelar aplicación
        </CancelAppButton>
      )
    );
  };

  renderIcon = () => {
    const { color } = this.props;
    return <IdCardIcon color={color} />;
  };

  defaultValues = (): {} => {
    return {
      discountCoupon: 'appa9660e'
    };
  };

  handleClickPromoCode = (values: {}, { resetForm }: any): void => {
    if (values && values.discountCoupon) {
      this.checkValidation();
    }
    HomeApplicationActions.validateHomeApplicationPromoCode(
      values.discountCoupon,
      { home_application_id: this.props.homeApplication.id },
      this.succesPromoCode,
      this.errorPromoCode
    );
  };

  payHomeApplicationCompletePromoCode = (promoCode: string) => {
    let payObject = {
      home_application_id: this.props.homeApplication.id,
      payment_method: 'promo',
      promo_code: 'appa9660e'
    };

    PaymentMethodActions.payApplicationByMethod(
      payObject,
      this.props.homeApplication.id,
      this.successPayByPromo
    );
  };

  successPayByPromo = () => {
    this.setState({
      errorPaymentPromo: false,
      correctPayment: true,
      paymentData: PaymentMethodStore.payApplicationByMethod()
    });
    window.location.href = `/h/inquilino/${this.props.homeApplication.id}/aplicacion-completada`;
  };

  succesPromoCode = (): void => {
    this.setState({
      succesCode: HomeApplicationStore.getCodeValidate(),
      errorCode: null
    });
    HomeApplicationStore.flushAssets();
    PaymentMethodStore.flushPaymentData();

    this.payHomeApplicationCompletePromoCode();
  };

  errorPromoCode = (): void => {
    alert('Hubo un error inesperado al finalizar la aplicación');
    this.setState({
      errorCode: HomeApplicationStore.getCodeValidateError(),
      succesCode: null
    });
  };

  render() {
    const { isForAdvisor, color } = this.props;
    return (
      <RenterApp.AppTabContainer>
        <BackButtonContainer
          onClick={this.props.onPrevStep}
          addMargin={isForAdvisor}
        >
          <BackButton text={'Referencias'} color={color} />
        </BackButtonContainer>
        <RenterApp.AppTabForm>
          <StyledForm addPadding={isForAdvisor}>
            <Title>Identificación oficial vigente</Title>
            <RenterApp.Text>
              Sube una fotografía de tu identificación oficial vigente, IFE, INE
              por delante y por detrás o pasaporte y FM en caso de ser
              extranjero.
            </RenterApp.Text>
            <UploadFiles
              Icon={this.renderIcon}
              IconError={IdCardIconRed}
              assetsLoaded={this.state.idFilesLoaded}
              uploadFileAction={this._uploadIdFile}
              onSelectFiles={this._onSelectIdFiles}
              onDeleteFileLoaded={this._handleDeleteIdFileLoaded}
              instruction={'Suelta aquí tus archivos'}
              textButton={'Añadir archivos'}
              haveError={this.state.idFilesValidationError}
              acceptFile={ValidFileTypesOnDocuments}
              color={color}
            />
            <ErrorMessage visible={this.state.idFilesValidationError}>
              {this.state.idFilesValidationError ? this.state.errorMessage : ''}
            </ErrorMessage>
            <RenterApp.EndSectionSpace />
            <Title>Comprobante de ingresos</Title>
            <RenterApp.Text>
              Sube comprobantes de los últimos 3 meses.{' '}
            </RenterApp.Text>
            <RenterApp.Text>
              Pueden ser recibos de nómina, honorarios, declaración anual o
              facturas, recuerda que deben ser ingresos mínimo 3 veces el valor
              de tu renta mensual.{' '}
            </RenterApp.Text>
            <UploadFiles
              Icon={this.renderIcon}
              IconError={IdCardIconRed}
              assetsLoaded={this.state.earningsFilesLoaded}
              uploadFileAction={this._uploadEarningsFile}
              onSelectFiles={this._onSelectEarningsFiles}
              onDeleteFileLoaded={this._handleDeleteEarningsFileLoaded}
              instruction={'Suelta aquí tus archivos'}
              textButton={'Añadir archivos'}
              haveError={this.state.earningsFilesValidationError}
              acceptFile={ValidFileTypesOnDocuments}
              color={color}
            />
            <ErrorMessage visible={this.state.earningsFilesValidationError}>
              {this.state.earningsFilesValidationError
                ? this.state.errorMessage
                : ''}
            </ErrorMessage>
            <RenterApp.EndSectionSpace />
          </StyledForm>
        </RenterApp.AppTabForm>
        {this.renderCancelButton()}
        <Formik
          onSubmit={this.handleClickPromoCode}
          initialValues={this.defaultValues()}
        >
          <Form>
            <RenterApp.SubmitButton
              disabled={
                this.resolveValidationFile(this.state.idFilesLoaded.length) ||
                !_.every(this.state.idFilesLoaded, asset => asset.file === null)
              }
              type="submit"
            >
              {this.props.appType === 'externo'
                ? 'Continuar'
                : 'Finalizar aplicación'}
            </RenterApp.SubmitButton>
          </Form>
        </Formik>
      </RenterApp.AppTabContainer>
    );
  }
}
